// The developer guide (ADR 0030): how a developer connects their AI tool to cortex and works with it. Its recipes
// are the CONNECTORS table and its change loop is CHANGE_LOOP, so the guide never says anything the Connect page,
// the MCP or a project's AGENTS.md says differently. Rendered with this cortex's addresses.
import { changeLoopMarkdown } from './change-loop.ts';
import { CONNECTORS, type ConnectTarget } from './connectors.ts';
import { readOnlyMcpUrl } from './endpoints.ts';

/** Each tool's recipe as guide sections. */
export function recipesMarkdown(t: ConnectTarget): string {
  const block = (code: string) => `\`\`\`\n${code}\n\`\`\``;
  return CONNECTORS.map((c) => {
    const alt = c.alternative(t);
    return [
      `### ${c.label}`,
      c.where(t),
      block(c.code(t)),
      ...c.notes(t),
      ...(alt ? [`${alt.label}:`, block(alt.code)] : []),
    ].join('\n\n');
  }).join('\n\n');
}

/** The full guide for this cortex (`t.mcp`: the one MCP's address). */
export function developerGuide(t: ConnectTarget): string {
  const url = t.mcp;
  return `Connect your AI coding tool (Claude Code, Codex, Cursor, VS Code, or any MCP client) to cortex once, and it works in every project you can open: it reads and changes them, files and follows Board cards, runs tests and opens pull requests. It always acts as you, with your access.

## Before you start

- A cortex account with access to the projects you work on.
- An AI tool that speaks MCP over Streamable HTTP with OAuth sign-in (all the tools below do).

## 1. Add cortex to your tool

Cortex's address is **${url}**. It reaches every project you can open. If the tool should reach only one project, use that project's own address instead (Settings → Connect an AI tool → "Only this project").

${recipesMarkdown(t)}

## 2. Sign in

The first time your tool uses cortex, your browser opens the cortex sign-in page:

1. Sign in with your cortex account.
2. Choose **read and write**, or **read-only** (the tool can then change nothing).
3. Allow. The tool continues on its own.

One sign-in covers every project you can open. You never paste a token.

**Read-only.** To let a tool look but never change anything, add \`?read_only=true\` to the address
(${readOnlyMcpUrl(url)}; the Read-only box on the Connect card does it). The tool then gets only the tools that
read, whatever you choose at sign-in, and cortex refuses a change from it as well.

## 3. Work

Ask your tool in plain words. For example:

- "List my cortex projects."
- "In project sales, what does the Board have in progress?"
- "Add a requirement to sales: invoices over 10,000 need a second approval, with a test that proves it."
- "File a Board card for that requirement and follow it until it is done."
- "Show me the open pull requests in sales and what their checks say."

Every project tool takes the \`project\` it works on (from \`list_projects\`) and the \`branch\` (default main).

How a change is made in cortex:

${changeLoopMarkdown({ tools: false })}

## Every project repository carries the setup

Cortex writes two files into each project's repository when the project is created (Settings → Repository status → AI tool files brings them up to date), so whoever clones it — a person or the agent fleet — finds how to work in it:

- \`AGENTS.md\`: the project's layout, its apps, how a change is made, and its MCP address. Every AI tool reads it (Codex, Cursor, Copilot, Gemini CLI, …).
- \`CLAUDE.md\`: one line, \`@AGENTS.md\`, so Claude Code reads the same file whatever sits in the folders above.

Cortex writes only its own part: the text between its markers in \`AGENTS.md\` and the import line in \`CLAUDE.md\`. Anything you add around them stays. Neither holds a token: every developer signs in with their own account, so each one works with their own access. Never commit a token.

## What it can and cannot do

- It can do only what you can do yourself: your role applies in every project, and projects you cannot open stay closed.
- A read-only sign-in changes nothing.
- It changes projects on branches and through pull requests.
- It merges a pull request only when its checks allow it, and only after you confirm that one merge. Where a project requires a reviewer's approval, the author cannot give it.
- It never reaches cortex's own administration.
- Your organization decides how far AI tools go (Settings → AI tools in your organization): as their person, read-only, or off. Every tool call is in the audit log: who, which tool, which project, how it ended. What you asked and what it answered are not.

## CI and scripts

A pipeline or script has no browser to sign in with. Give it an API token made for an AI tool instead:

1. Settings → API tokens → **Create token**, with the scope **mcp:read** (read-only) or **mcp:write** (read and write). It belongs to your organization and expires when you choose.
2. Store it in your CI's secrets, never in the repository.
3. Point the MCP client at ${url} and send the token as a header: \`Authorization: Bearer ctk_…\`. No sign-in page opens.

Such a token reaches the projects of its organization (\`list_projects\` names them) with your access. It never acts as you on a pull request: it cannot approve or merge, and its changes are recorded as the token's. Revoke it under Settings → API tokens.

## Tool groups

Besides the project tools, the tool sees cortex's own operations in groups. By default these are the Board, requirements and their sources, tests, pull requests and code, branch previews, the fleet's sessions, and the project's Deployments (with your own sign-in: deploying a version asks you first). To choose groups, add them to the address: \`${url}?toolsets=board,docs\`. The tool sees only what your sign-in may call.

## Disconnect

Settings → Connect an AI tool → Connected tools → **Disconnect**. The tool stops at once in every project. To use it again, sign in again.

## If something goes wrong

- **The browser does not open, or the sign-in loops.** Remove cortex from the tool, add it again and retry. A corporate proxy must let the tool reach ${url}.
- **"The token is bound to project …"** You signed in through a project's own address. Use ${url} to reach every project, or keep that address for that project only.
- **A tool you expect is missing.** You signed in read-only, or its group is not in the address's \`toolsets\`.
- **A merge is refused.** Its checks are not passing, or the project requires an approval you cannot give as the author. The answer says which.
- **"AI tools are switched off for this organization" or "lets AI tools only read".** An org admin chose that under Settings → AI tools in your organization.
`;
}
